import { Token, TokenType, LexerOptions } from './types'
import {
  hasData,
  peekChar,
  eatWhiteSpace,
  tokenCreate,
  eatChar,
  peekAt,
  isInteger,
  eatNumber,
  eatStringLiteral,
  isIdentifierStart,
  eatIdentifer,
  eatNewlines,
  eatMultiLineComment,
  eatSingleLineComment,
  eatNewline,
} from './helpers'
import { throwError } from '../throw-error'
import { Lexer } from '../lexer'

type State =
  | 'initial'
  | 'comment'
  | 'inComment'

export class ASMLexer extends Lexer<TokenType, State> {
  ignoreWhiteSpace: boolean
  ignoreNewLine: boolean
  ignoreComments: boolean

  constructor(source: string, options: LexerOptions = {}) {
    super(source, options)

    this.state = 'initial'
    this.ignoreWhiteSpace = options.ignoreWhiteSpace ?? true
    this.ignoreNewLine = options.ignoreNewline ?? true
    this.ignoreComments = options.ignoreComments ?? true
  }

  next(): Token {
    if (!hasData(this))
      return {
        type: 'eof',
        value: '',
      }

    const state = this.state
    const nextChar = peekChar(this)

    switch(state) {
      case 'comment':
      {
        return tokenCreate('comment', eatMultiLineComment(this))
      }
      case 'inComment':
      {
        if (nextChar === '\n') {
          return tokenCreate('newline', eatNewline(this))
        }

        return tokenCreate('comment', eatMultiLineComment(this))
      }
      default:
      {
        switch (nextChar) {
          case ' ':
          case '\t':
          case '\r': {
            const value = eatWhiteSpace(this)

            if (this.ignoreWhiteSpace) return this.next()

            return tokenCreate('whitespace', value)
          }
          case '\n': {
            if (this.ignoreNewLine) {
              eatNewlines(this)

              return this.next()
            }

            return tokenCreate('newline', eatNewline(this))
          }
          case '+': {
            return tokenCreate('plus', eatChar(this))
          }
          case '-':
            return tokenCreate('min', eatChar(this))
          case '%':
            return tokenCreate('mod', eatChar(this))
          case '*':
            return tokenCreate('mul', eatChar(this))
          case '@': {
            const comment = eatSingleLineComment(this)

            if (this.ignoreComments) return this.next()

            return tokenCreate('comment', comment)
          }
          case '/': {
            const peekedChar = peekAt(this, 1)

            if (peekedChar === '*') {
              if (this.ignoreComments) return this.next()

              this.state = 'comment'

              return this.next()
            }

            return tokenCreate('div', eatChar(this))
          }
          case '=': {
            if (peekAt(this, 1) === '=') return tokenCreate('eq', eatChar(this, 2))

            return tokenCreate('assign', eatChar(this))
          }
          case ':':
            return tokenCreate('colon', eatChar(this))
          case '(':
            return tokenCreate('left_paren', eatChar(this))
          case ')':
            return tokenCreate('right_paren', eatChar(this))
          case ',':
            return tokenCreate('comma', eatChar(this))
          case '.': {
            if (isInteger(peekAt(this, 1))) {
              return tokenCreate('number', eatChar(this) + eatNumber(this))
            }

            return tokenCreate('dot', eatChar(this))
          }
          case '"': {
            const { value, error } = eatStringLiteral(this, nextChar)

            if (error) {
              if (this.throws)
                throwError(
                  'Unterminated string literal',
                  this.line,
                  this.col,
                  this.index,
                  this.source,
                  this.source[this.index] ?? ''
                )

              return tokenCreate('string_literal', value, 'Unterminated string literal')
            }

            return tokenCreate('string_literal', value)
          }
          case "'": {
            const { value, error } = eatStringLiteral(this, nextChar)

            if (error) {
              if (this.throws)
                throwError(
                  'Unterminated string literal',
                  this.line,
                  this.col,
                  this.index,
                  this.source,
                  this.source[this.index] ?? ''
                )

              return tokenCreate('character_constant', value, 'Unterminated character constant')
            }

            return tokenCreate('character_constant', value)
          }
          case '[':
            return tokenCreate('left_brack', eatChar(this))
          case ']':
            return tokenCreate('right_brack', eatChar(this))
          case '{':
            return tokenCreate('left_curl_brace', eatChar(this))
          case '}':
            return tokenCreate('right_curl_brace', eatChar(this))
          case '#':
            return tokenCreate('pound', eatChar(this))
          default: {
            if (isInteger(nextChar)) return tokenCreate('number', eatNumber(this))

            if (isIdentifierStart(nextChar)) {
              const value = eatIdentifer(this)

              return tokenCreate('symbol', value)
            }
          }

          const errorMessage = `Lexer: Invalid syntax "${this.source[this.index]}"`

          if (this.throws) {
            return throwError(errorMessage, this.line, this.col, this.index, this.source, nextChar)
          }

          return tokenCreate('error', eatChar(this), errorMessage)
        }
      }
    }
  }

  *[Symbol.iterator]() {
    while (true) {
      const token = this.next()

      if (token.type === 'eof') break

      yield token
    }
  }
}
